import { useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories, subcategories } from '@/mocks/movies';

const categoryNames: Record<string, string> = {};
categories.forEach((c) => { categoryNames[c.slug] = c.name; });

const subcatNames: Record<string, Record<string, string>> = {};
Object.entries(subcategories).forEach(([cat, subs]) => {
  subcatNames[cat] = {};
  subs.forEach((s) => { subcatNames[cat][s.slug] = s.name; });
});

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSub, setActiveSub] = useState('all');
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'hot'>('latest');

  const catName = categoryNames[slug || ''] || '分类';
  const subs = subcategories[slug || ''] || [];

  const filteredMovies = useMemo(() => {
    let list = movies.filter((m) => m.category === slug);
    if (activeSub !== 'all') {
      list = list.filter((m) => m.subcategory === activeSub);
    }
    if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'hot') {
      list = [...list].sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0));
    }
    return list;
  }, [slug, activeSub, sortBy]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `四虎影院 - ${catName}`,
    description: `四虎影院${catName}频道，海量高清${catName}资源在线观看。`,
    url: `https://jin-yuan.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '四虎影院',
      url: 'https://jin-yuan.cn/',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: catName },
              ]}
            />

            <div className="mt-4 mb-6">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{catName}</h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredMovies.length} 部影片
              </p>
            </div>

            {subs.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-6">
                {subs.map((sub) => (
                  <button
                    key={sub.slug}
                    onClick={() => setActiveSub(sub.slug)}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                      activeSub === sub.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 bg-background-100 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mb-5">
              <span className="text-sm text-foreground-500">
                当前：{subcatNames[slug || '']?.[activeSub] || activeSub}
              </span>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {([
                  { key: 'latest', label: '最新' },
                  { key: 'rating', label: '评分' },
                  { key: 'hot', label: '热门' },
                ] as const).map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key)}
                    className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
                      sortBy === s.key
                        ? 'bg-background-50 text-foreground-950 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} size="normal" />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-foreground-500">
                <i className="ri-movie-2-line text-5xl mb-3"></i>
                <p>该分类暂无影片</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}